import { useEffect, useMemo, useState } from "react";
import type { Player, MatchConfig } from "../../shared/types";
import { WORM_COLOR_IDS } from "../../shared/worm-colors";
import { keyPairs } from "../../shared/key-util.js";
import * as clientConstants from "../../shared/constants.js";
import { DEFAULT_SPAWN_CHANCE, DEFAULT_WORM_SPEED } from "../../shared/powerups";
import { getLocalPlayer } from "../../shared/local-players";
import { DEFAULT_MAP_VALUE, createMapFromValue, normalizeMapValue } from "./maps";
import { SKILL_BY_DIFFICULTY, type Difficulty } from "./theme";
import { PRESETS, presetDisabled, derivePlaystyle, type Preset } from "./presets";
import { loadLocalSetup, saveLocalSetup } from "./localSetupStore";

export type WinMode = "score" | "rounds";

let SEQ = 1;
const nextId = () => "p_" + SEQ++;

// Cap on humans sharing one computer: at most 10 (a keyboard only registers so
// many simultaneous keys), and never more than the key schemes we can hand out.
export const MAX_COUCH_SCHEMES = Math.min(10, keyPairs.length);
// Hard cap on total worms (humans + bots). Colors are guaranteed up to this many
// (see worm-colors.ts); core packs worms tighter on small arenas rather than fail.
export const MAX_PLAYERS = 40;

const AVAILABLE_NAMES: string[] = clientConstants.AVAILABLE_NAMES;

function unusedColor(players: { colorId: string }[]): string {
  return WORM_COLOR_IDS.find((id) => players.every((p) => p.colorId !== id)) || WORM_COLOR_IDS[players.length % WORM_COLOR_IDS.length];
}
function unusedName(players: { name: string }[]): string {
  const used = new Set(players.map((p) => p.name));
  const free = AVAILABLE_NAMES.filter((n) => !used.has(n));
  return free.length ? free[Math.floor(Math.random() * free.length)] : "Worm " + (players.length + 1);
}
function unusedKeyPair(players: Player[]): { left: string | null; right: string | null } {
  const free = keyPairs.filter((b: any) => players.every((p) => p.left !== b.left && p.right !== b.left && p.left !== b.right && p.right !== b.right));
  return free[0] || { left: null, right: null };
}

// Difficulty label a bot's skill maps back to (for the roster badge).
export function skillToDifficulty(skill?: number): Difficulty {
  if (skill == null) return "Medium";
  if (skill <= SKILL_BY_DIFFICULTY.Easy) return "Easy";
  if (skill <= SKILL_BY_DIFFICULTY.Medium) return "Medium";
  return "Hard";
}

export interface MatchSetup {
  players: Player[];
  max: number;
  fill: Difficulty;
  mapValue: string;
  winMode: WinMode;
  score: number;
  rounds: number;
  disabledPowerUps: string[];
  spawnChance: number;
  wormSpeed: number;
  playstyle: string; // derived from spawn + power-ups (a preset name / Custom / No powerups)

  online: boolean; // online lobby (has max players + auto-fill) vs local play
  seatCap: number; // max seats you can add (max online, MAX_PLAYERS local)
  humanCount: number;
  autoFillCount: number;
  // The exact keys the next couch player would get (same fn addLocalPlayer uses, so
  // the button preview never drifts from what's actually assigned). null.left if none.
  nextKeys: { left: string | null; right: string | null };

  setFill: (d: Difficulty) => void;
  setMapValue: (v: string) => void;
  setWinMode: (m: WinMode) => void;
  setScore: (n: number) => void;
  setRounds: (n: number) => void;
  setDisabledPowerUps: (keys: string[]) => void;
  setSpawnChance: (n: number) => void;
  setWormSpeed: (n: number) => void;
  applyPreset: (p: Preset) => void;

  incMax: () => void;
  decMax: () => void;
  addLocalPlayer: (cap: number) => void;
  addBot: (difficulty: Exclude<Difficulty, "Off">) => void;
  removePlayer: (id: string) => void;
  setColor: (id: string, colorId: string) => void;
  setName: (id: string, name: string) => void;
  setKey: (id: string, side: "left" | "right", key: string) => void;

  buildMatchConfig: () => MatchConfig;
}

// Central state + logic for a match-setup screen (Play locally / Create lobby).
// Holds the real roster (Player[]) plus map / winning-condition / power-up
// settings, and builds a core MatchConfig (auto-filling empty seats with bots).
// `extras`: more humans on this keyboard to seat from the start (an online room
// entered as "Several on this keyboard").
export function useMatchSetup(online = false, extras: Array<{ name: string; colorId: string; left: string | null; right: string | null; flag?: string | null }> = []): MatchSetup {
  // Lead human ("You") seeds from the shared local-player store so name/color/keys
  // match the other screens; falls back to a random handle + first scheme.
  // Local play restores your last setup (roster + settings) so "play again" keeps
  // what you had. Online lobbies always start fresh.
  const saved = useMemo(() => (online ? null : loadLocalSetup()), [online]);
  const [players, setPlayers] = useState<Player[]>(() => {
    const stored = getLocalPlayer(0, unusedName([]));
    if (saved?.players?.length) {
      // The saved roster is a snapshot, but cosmetics are equipped in the shop
      // (which writes the local-player store), so re-read those for the lead
      // human or a newly equipped head/flag/celebration never reaches local play.
      let leadDone = false;
      return saved.players.map((p) => {
        if (p.type === "human" && !leadDone) {
          leadDone = true;
          return { ...p, id: nextId(), flag: stored.flag ?? null, victoryAnim: stored.victoryAnim ?? null, headStyle: stored.headStyle ?? null };
        }
        return { ...p, id: nextId() };
      });
    }
    const you: Player = { id: nextId(), type: "human", name: stored.name, colorId: stored.colorId, left: stored.left ?? keyPairs[0].left, right: stored.right ?? keyPairs[0].right, flag: stored.flag ?? null, victoryAnim: stored.victoryAnim ?? null, headStyle: stored.headStyle ?? null };
    // a fresh lobby is the host and anyone sharing the keyboard; others join / auto-fill
    if (online) return [you, ...extras.map((e): Player => ({ id: nextId(), type: "human", name: e.name, colorId: e.colorId, left: e.left, right: e.right, flag: e.flag ?? null, victoryAnim: null, headStyle: null }))];
    // Local play has no auto-fill, so seed one bot to have an opponent to start against.
    const bot: Player = { id: nextId(), type: "bot", name: unusedName([you]), colorId: unusedColor([you]), left: null, right: null, skill: SKILL_BY_DIFFICULTY.Medium, victoryAnim: null, headStyle: null }; // no cosmetics offline
    return [you, bot];
  });
  const [max, setMax] = useState(saved?.max ?? 6);
  const [fill, setFill] = useState<Difficulty>(saved?.fill ?? "Medium");
  // (a saved arena snaps to its shape at the nearest standard size)
  const [mapValue, setMapValue] = useState(saved?.mapValue ? normalizeMapValue(saved.mapValue) : DEFAULT_MAP_VALUE);
  const [winMode, setWinMode] = useState<WinMode>(saved?.winMode ?? "score");
  const [score, setScore] = useState(saved?.score ?? 10);
  const [rounds, setRounds] = useState(saved?.rounds ?? 10);
  const [disabledPowerUps, setDisabledPowerUps] = useState<string[]>(saved?.disabledPowerUps ?? []);
  const [spawnChance, setSpawnChance] = useState(saved?.spawnChance ?? DEFAULT_SPAWN_CHANCE);
  const [wormSpeed, setWormSpeed] = useState(saved?.wormSpeed ?? DEFAULT_WORM_SPEED);

  // Persist the local setup on every change so it's there next time.
  useEffect(() => {
    if (online) return;
    saveLocalSetup({ players, max, fill, mapValue, winMode, score, rounds, disabledPowerUps, spawnChance, wormSpeed });
  }, [online, players, max, fill, mapValue, winMode, score, rounds, disabledPowerUps, spawnChance, wormSpeed]);

  // The playstyle is derived from the current power-ups + spawn rate, not stored,
  // so it always reflects reality (tweaking a power-up flips it to Custom).
  const playstyle = derivePlaystyle(disabledPowerUps, spawnChance);

  const humanCount = useMemo(() => players.filter((p) => p.type === "human").length, [players]);
  // Max players + auto-fill are a lobby concept (online). Local play just uses the
  // explicit roster (capped at MAX_PLAYERS), no empty seats to fill.
  const seatCap = online ? max : MAX_PLAYERS;
  const autoFillCount = online && fill !== "Off" ? Math.max(0, max - players.length) : 0;
  // What addLocalPlayer would assign next (the first unused key scheme). Used by the
  // add button's preview so it always matches the keys the new player actually gets.
  const nextKeys = useMemo(() => unusedKeyPair(players), [players]);

  function incMax() {
    setMax((m) => Math.min(MAX_PLAYERS, m + 1));
  }
  function decMax() {
    // Max can always go down to 2. If the new cap is below the explicit roster,
    // drop entries to fit in priority order: bots first, then couch players
    // (non-host humans), last-added first. The host is never removed. (Auto-fill
    // bots aren't in `players` - they just shrink on their own as the cap drops.)
    const target = Math.max(2, max - 1);
    setMax(target);
    if (players.length > target) {
      const over = players.length - target;
      const remove = new Set<number>();
      for (let i = players.length - 1; i >= 0 && remove.size < over; i--) if (players[i].type === "bot") remove.add(i);
      for (let i = players.length - 1; i >= 0 && remove.size < over; i--) if (players[i].type === "human" && i !== 0) remove.add(i);
      setPlayers((prev) => prev.filter((_, i) => !remove.has(i)));
    }
  }

  function addLocalPlayer(cap: number) {
    setPlayers((prev) => {
      const humans = prev.filter((p) => p.type === "human");
      if (humans.length >= cap || prev.length >= seatCap) return prev;
      const kp = unusedKeyPair(prev);
      return [...prev, { id: nextId(), type: "human", name: unusedName(prev), colorId: unusedColor(prev), left: kp.left, right: kp.right }];
    });
  }

  function addBot(difficulty: Exclude<Difficulty, "Off">) {
    setPlayers((prev) => {
      if (prev.length >= seatCap) return prev;
      return [...prev, { id: nextId(), type: "bot", name: unusedName(prev), colorId: unusedColor(prev), left: null, right: null, skill: SKILL_BY_DIFFICULTY[difficulty], victoryAnim: null, headStyle: null }];
    });
  }

  function removePlayer(id: string) {
    setPlayers((prev) => (prev.length <= 1 ? prev : prev.filter((p) => p.id !== id)));
  }

  function setColor(id: string, colorId: string) {
    setPlayers((prev) => {
      const target = prev.find((p) => p.id === id);
      if (!target) return prev;
      const old = target.colorId;
      return prev.map((p) => (p.id === id ? { ...p, colorId } : p.colorId === colorId ? { ...p, colorId: old } : p));
    });
  }
  function setName(id: string, name: string) {
    setPlayers((prev) => prev.map((p) => (p.id === id ? { ...p, name: name.substring(0, 16) } : p)));
  }
  function setKey(id: string, side: "left" | "right", key: string) {
    setPlayers((prev) =>
      prev.map((p) => {
        const u = { ...p };
        if (u.left === key) u.left = null;
        if (u.right === key) u.right = null;
        if (u.id === id) u[side] = key;
        return u;
      })
    );
  }

  function applyPreset(p: Preset) {
    // Playstyles sit right above spawn rate + power-ups and only change those.
    // Worm speed is an independent basic setting above. The highlight is derived,
    // so no selection to store - just apply the settings.
    setSpawnChance(p.spawn);
    setDisabledPowerUps(presetDisabled(p));
  }

  function buildMatchConfig(): MatchConfig {
    const roster: Player[] = players.map((p) => ({ ...p }));
    for (let i = 0; i < autoFillCount; i++) {
      roster.push({ id: nextId(), type: "bot", name: unusedName(roster), colorId: unusedColor(roster), left: null, right: null, skill: fill === "Off" ? undefined : SKILL_BY_DIFFICULTY[fill], victoryAnim: null, headStyle: null });
    }
    return {
      players: roster,
      map: createMapFromValue(mapValue),
      maxScore: winMode === "score" ? score : 9999,
      maxRounds: winMode === "rounds" ? rounds : undefined,
      disabledPowerUps,
      powerUpSpawnChance: spawnChance,
      wormSpeed,
    };
  }

  return {
    players, max, fill, mapValue, winMode, score, rounds, disabledPowerUps, spawnChance, wormSpeed, playstyle,
    online, seatCap, humanCount, autoFillCount, nextKeys,
    setFill, setMapValue, setWinMode, setScore, setRounds,
    setDisabledPowerUps, setSpawnChance, setWormSpeed,
    applyPreset,
    incMax, decMax, addLocalPlayer, addBot, removePlayer, setColor, setName, setKey,
    buildMatchConfig,
  };
}

export { PRESETS };
